/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useSnapshot } from 'valtio';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import { useRepairFacilities } from '@components/service-and-repair/providers/repair-facilities/repair-facilities-provider';
import { SarStore } from '@store/sar-store';
import { Warranty, SubCategory, Category } from '../../constants';
import { IssueSubmitted } from './coverage-issue-submitted';
import { ContactUs } from './coverage-contact-us';
import { Sorry } from './coverage-sorry';
import { Good } from './coverage-good';
import { CoverageScreenType } from './get-logic/coverage-type';
import { getCoverageTextPlain } from './get-logic/coverage-text-plain';
import { useCoverage } from './use-coverage';
import { LoadingScreen } from '../loading-screen';
import { useUserSelections, CoverageTypeSentinel } from '../../providers/user-selections';
import { ROUTES, useRouter } from '../../providers/router';
import { ResultTileProps } from '../service-repair-center/result-tile';

export type UserCoverageAndSelections = {
  warranty: Warranty;
  category: Category;
  subcategory: SubCategory;
  deductible: number;
};

export type TileProps = {
  title: string;
  text: string;
  link: string;
  // treatment
  description: string;
  buttonType: ResultTileProps['buttonType'] | any;
  disclaimer?: string;
};

export type CoveragePageProps = {
  coverageScreenType?: CoverageScreenType;
  title?: string;
  description?: string[] | JSX.Element;
  button?: {
    text: string;
    link?: string;
    buttonType?: string;
    closeModal?: boolean;
    viewMoreButton?: boolean;
  };
  deductible?: DeductibleProps;
  outboundLinks?: TileProps[];
  showPreferredPartnersForm?: boolean;
};

export type DeductibleProps = {
  inNetworkDeductible?: number;
  outOfNetworkDeductible?: number;
  show: boolean;
};

export type CoverageProps = {};

export const Coverage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    saveUserSelections,
    setHasUploadError,
    userSelectionsSubmitted,
    setUserSelectionsSubmitted,
    userFeedData,
    userSelections,
  } = useUserSelections();
  const { deductibleState } = useSnapshot(SarStore);
  const { fetchRepairFacilities } = useRepairFacilities();
  const { determineCoverage, warranties, userCoverageAndSelections, hasCoverage, deductible, daysLeft, carvanaCare } = useCoverage();
  const { goToRoute } = useRouter();
  const { coverageScreenType } = determineCoverage || {};
  const checkWarrantyExpired = isWarrantyExpired(userFeedData.coverage);
  const saveUserDetailsInvocationCount = useRef(0);

  const saveUserDetails = async (coverageScreen: CoverageScreenType) => {
    setIsLoading(true);

    let coverageText = '';
    try {
      coverageText = getCoverageTextPlain(coverageScreen, userCoverageAndSelections, userFeedData?.make || '', deductible, daysLeft, carvanaCare);
    } catch (error) {
      datadogRum.addAction('sar-coverage-text-error', {
        type: 'warning',
        error: error instanceof Error ? error.message : String(error),
        sarSessionId: SarStore.sarSessionId,
      });
    }

    const response = await saveUserSelections(coverageScreen, {
      coverageText,
      coverageType: coverageScreen === CoverageScreenType.cosmeticSubmitted
        ? CoverageTypeSentinel.cosmeticReview
        : (userCoverageAndSelections?.warranty ?? CoverageTypeSentinel.notCovered),
    });

    if (!response) {
      datadogRum.addAction(AnalyticEvents.sarSaveUserDetailsNoResponse, {
        type: 'warning',
        coverageScreen,
        sarSessionId: SarStore.sarSessionId,
        message: 'saveUserSelections returned null/undefined - proceeding without ASR',
      });
      if (coverageScreen === CoverageScreenType.cosmeticSubmitted) {
        setHasUploadError(true);
        goToRoute(ROUTES.IMAGE_UPLOAD);
      }
      setIsLoading(false);
      return;
    }
    setUserSelectionsSubmitted(true);

    const shouldRetrieveRepairFacilities = !!coverageScreen && determineCoverage?.showPreferredPartnersForm;

    if (shouldRetrieveRepairFacilities) {
      await fetchRepairFacilities(undefined, response.content?.asrId);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (userSelectionsSubmitted) {
      setIsLoading(false);
      return;
    }

    if (determineCoverage?.coverageScreenType) {
      saveUserDetailsInvocationCount.current += 1;
      datadogRum.addAction(AnalyticEvents.sarCoverageScreenSaveInvoked, {
        type: 'start',
        invocationCount: saveUserDetailsInvocationCount.current,
        coverageScreenType: determineCoverage.coverageScreenType,
        sarSessionId: SarStore.sarSessionId,
        userSelectionsSubmitted,
      });
      saveUserDetails(determineCoverage.coverageScreenType);
    }
  }, [determineCoverage?.coverageScreenType]);

  useEffect(() => {
    if (!determineCoverage || isLoading) return;

    const subSubCategoryValue =
      userCoverageAndSelections.subcategory === SubCategory.Glass
        ? userSelections.location
        : userCoverageAndSelections.subsubcategory;

    const props = {
      coverage: hasCoverage,
      category: userCoverageAndSelections.category,
      coverageScreen: determineCoverage?.coverageScreenType,
      subCategory: userCoverageAndSelections.subcategory,
      subSubCategory: subSubCategoryValue,
      inNetworkDeductibleAmount: deductible?.inNetwork,
      outOfNetworkDeductibleAmount: deductible?.outOfNetwork,
      links: determineCoverage?.outboundLinks?.map((link) => link.link),
      oemBasic: !checkWarrantyExpired(Warranty.OEMBasic),
      oemPowertrain: !checkWarrantyExpired(Warranty.OEMPowerTrain),
      limitedWarranty: !checkWarrantyExpired(Warranty.LimitedWarranty),
      carvanaCareEssential: !checkWarrantyExpired(Warranty.CarvanaCareEssential),
      carvanaCarePlus: !checkWarrantyExpired(Warranty.CarvanaCarePlus),
      carvanaCarePremier: !checkWarrantyExpired(Warranty.CarvanaCarePremier),
      carvanaCareCosmetic: !checkWarrantyExpired(Warranty.CarvanaCareCosmetic),
      sevenDay: !checkWarrantyExpired(Warranty.SevenDay),
      thirtyDay: !checkWarrantyExpired(Warranty.ThirtyDay),
      type: determineCoverage?.coverageScreenType,
      deductibleState: deductibleState || '',
    };
    Analytics.trackEvent(AnalyticEvents.sarCoverageScreenViewed, props);
    datadogRum.addAction(AnalyticEvents.sarCoverageScreenViewed, props);
  }, [determineCoverage, isLoading]);

  if (isLoading) return <LoadingScreen />;

  if (!coverageScreenType)
    return <div>There has been an unidentified error. Please close the modal and try again.</div>;

  switch (coverageScreenType) {
    case CoverageScreenType.limited100Covered:
    case CoverageScreenType.thirtyDayCovered:
    case CoverageScreenType.manufacturerCovered:
    case CoverageScreenType.carvanaCareCovered:
    case CoverageScreenType.carvanaCareOilTireCovered:
    case CoverageScreenType.conditionallyCoveredBrakes:
    case CoverageScreenType.carvanaCareRoadHazardCovered:
    case CoverageScreenType.carvanaCareOilChangeCovered:
    case CoverageScreenType.carvanaCareDentCovered:
      return <Good {...determineCoverage} />;
    case CoverageScreenType.notCovered:
    case CoverageScreenType.notCoveredCosmetic:
    case CoverageScreenType.notCoveredGlass:
    case CoverageScreenType.notCoveredAdditionalCosmetic:
    case CoverageScreenType.notCoveredAdditionalKey:
    case CoverageScreenType.notCoveredMissingAccessories:
    case CoverageScreenType.carvanaCareRoadHazardNotCovered:
    case CoverageScreenType.carvanaCareOilChangeNotCovered:
      return <Sorry {...determineCoverage} />;
    case CoverageScreenType.contactUs:
      return <ContactUs />;
    case CoverageScreenType.cosmeticSubmitted:
      return <IssueSubmitted />;
    default:
      return <ContactUs />;
  }
};

export const generateKey = () => Math.random().toString(36).substring(7);

const isWarrantyExpired = (warranties: any[]) => (warranty: Warranty) => {
  const hasWarranty = warranties?.find((w) => w?.type === warranty);
  return hasWarranty ? hasWarranty?.isExpired : true;
};
